import { PromiseClient } from 'hub-http/adapters/promiseClient';
import http from 'hub-http/clients/apiClient';
import { Map as ImmutableMap, List, fromJS } from 'immutable';
import { KnowledgeArticle } from 'knowledge-content-types/js/KnowledgeContent/types';

import {
  GetArticlesAllPropsResponse,
  GetArticlesV3Response,
  GetCombinedKBs,
  GetSingleArticleV3,
  NormalizedArticle,
  GetNormalizedArticlesResponse,
} from '../types';

const baseUrl = 'knowledge-content/v1/knowledge-articles';
const baseUrlV3 = 'knowledge-content/v2/knowledge-article';

const filterPublished = (articles: KnowledgeArticle[]) =>
  articles.filter((a) => a.currentState === 'PUBLISHED');

const formatResponse = (
  response: GetArticlesAllPropsResponse | GetNormalizedArticlesResponse
) => {
  // @ts-expect-error ts-migrate FIXME some props don't exist
  const { objects, offset, count, total, hasMore } = response;

  return ImmutableMap({
    hasMore: hasMore || false,
    offset: offset || 0,
    count: count || objects.length,
    total: total || objects.length,
    results: List(filterPublished(objects as any[])).map((article) =>
      fromJS(article)
    ),
  });
};

export const defaultApiWrapper = (
  apiClient: PromiseClient,
  knowledgeBaseVersion?: '2' | '3',
  kbId?: number | number[]
) => ({
  fetchRenderedContent({
    id,
    version: kbVersion,
  }: {
    id: string | number;
    version?: '2' | '3';
  }) {
    return kbVersion === '3'
      ? getArticleV3(apiClient, id)
      : getArticle(apiClient, id);
  },
  async searchArticles({
    count,
    offset,
    query,
  }: {
    count: number;
    offset: number;
    query: string;
  }) {
    if (kbId) {
      const kbIds = Array.isArray(kbId) ? kbId : [kbId];
      const response =
        knowledgeBaseVersion === '3'
          ? await getArticlesV3({
              apiClient,
              count,
              kbIds,
              offset,
              query,
            })
          : await getArticles(apiClient, count, offset, query, kbIds[0]);
      return formatResponse(response);
    }

    const [combinedKBs, response, responseV3] = await Promise.all([
      getCombinedKBs(apiClient),
      getArticles(apiClient, count, offset, query),
      getArticlesV3({
        apiClient,
        count,
        offset,
        query,
      }),
    ]);

    // Produce a set of all KB IDs, including translations
    const allKbIds = new Set([
      ...combinedKBs.legacyKnowledgeBases.map((kb) => kb.id),
      ...combinedKBs.scpKnowledgeBases.flatMap((kb) => [
        kb.values.hs_id,
        ...Object.values(kb.translations).map((t) => t.values.hs_id),
      ]),
    ]);

    return formatResponse({
      objects: [
        ...response.objects.filter((a) => allKbIds.has(a.contentGroupId)),
        ...responseV3.objects.filter((a) => allKbIds.has(a.contentGroupId)),
      ],
      limit: count,
      offset,
      total: response.total + responseV3.total,
      totalCount: response.totalCount + responseV3.totalCount,
    });
  },
});

export const searchArticles = ({
  count,
  offset,
  query,
}: {
  count: number;
  offset: number;
  query: string;
}) => {
  return http
    .get<GetArticlesAllPropsResponse>(baseUrl, {
      query: {
        limit: count,
        offset,
        name__icontains: query,
        order: '-updated',
        state__eq: 'PUBLISHED',
        archived: false,
      },
    })
    .then(formatResponse);
};

async function getArticle(apiClient: PromiseClient, id: string | number) {
  const results = await apiClient.get<GetArticlesAllPropsResponse>(baseUrl, {
    query: { id },
  });

  const article = results.objects[0];
  const { absoluteUrl, title } = article;
  return {
    body: title,
    htmlBody: `<a href=${absoluteUrl}>${title}</p>`,
  };
}
async function getArticleV3(apiClient: PromiseClient, id: string | number) {
  const { values: article } = await apiClient.get<GetSingleArticleV3>(
    `${baseUrlV3}/${id}`
  );

  return {
    body: article.hs_name,
    htmlBody: `<a href=${article.hs_absolute_url}>${article.hs_name}</p>`,
  };
}

async function getArticles(
  apiClient: PromiseClient,
  count: number,
  offset: number,
  query: string,
  kbId?: number
): Promise<GetNormalizedArticlesResponse> {
  const response = await apiClient.get<GetArticlesAllPropsResponse>(baseUrl, {
    query: {
      limit: count,
      offset,
      name__icontains: query,
      order: '-updated',
      state__eq: 'PUBLISHED',
      archived: false,
      ...(kbId ? { contentGroupId: kbId } : {}),
    },
  });

  const normalizedObjects: NormalizedArticle[] = response.objects.map((a) => ({
    ...a,
    version: '2',
  }));

  return {
    limit: response.limit,
    offset: response.offset,
    total: response.total,
    totalCount: response.totalCount,
    objects: normalizedObjects,
  };
}
async function getArticlesV3({
  apiClient,
  count,
  kbIds,
  offset,
  query,
}: {
  apiClient: PromiseClient;
  count: number;
  kbIds?: number[];
  offset: number;
  query: string;
}): Promise<GetNormalizedArticlesResponse> {
  const response = await apiClient.get<GetArticlesV3Response>(baseUrlV3, {
    query: {
      hs_archived_in_dashboard__eq: 0,
      hs_name__icontains: query,
      hs_published_at__gt: 0,
      limit: count,
      offset,
      order: '-hs_updated_at',
      ...(kbIds && kbIds.length > 0 ? { hs_group_id__in: kbIds } : {}),
    },
  });

  const mapped: NormalizedArticle[] = [];

  response.results.forEach(({ values: article }) => {
    mapped.push({
      currentState: article.hs_state,
      contentGroupId: article.hs_group_id,
      id: article.hs_id,
      title: article.hs_name,
      version: '3',
      language: article.hs_language,
      absoluteUrl: article.hs_absolute_url,
      articleSummary: article.hs_summary,
    });
  });

  if (query || !(kbIds && kbIds.length > 0)) {
    response.results.forEach(({ values: article, translations }) => {
      if (translations) {
        Object.values(translations).forEach(
          ({ values: translationArticle }) => {
            if (translationArticle.hs_state === 'PUBLISHED') {
              mapped.push({
                currentState: translationArticle.hs_state,
                contentGroupId: translationArticle.hs_group_id,
                id: translationArticle.hs_id,
                title: translationArticle.hs_name,
                version: '3',
                language: translationArticle.hs_language,
                absoluteUrl: translationArticle.hs_absolute_url,
                articleSummary: translationArticle.hs_summary,
                isTranslation: true,
                originalArticleId: article.hs_id,
              });
            }
          }
        );
      }
    });
  }

  const seen = new Set<number>();
  const deduplicated = mapped.filter((article) => {
    if (seen.has(article.id)) return false;
    seen.add(article.id);
    return true;
  });

  return {
    limit: count,
    offset,
    total: response.total,
    totalCount: response.total,
    objects: deduplicated,
  };
}

function getCombinedKBs(apiClient: PromiseClient) {
  return apiClient.get<GetCombinedKBs>(
    'knowledge-content/v2/migration/combined'
  );
}
